import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../core/authentication/authentication.service';

@Injectable({ providedIn: 'root' })
export class CampaignHelperService {
    allChannelsGrouped = null;
    allMultiResolutionChannelsGrouped = null;

    constructor(
        private http: HttpClient,
        private authenticationService: AuthenticationService
    ) { }

    getIndustries() {
        return this.http.get(`${environment.apiUrl}/listing/industry/`);
    }

    getCampaignAvailableLocations(campaignId) {
        return this.http.post(`${environment.apiUrl}/campaign/location/get/${campaignId}`, {
            activeRole: this.authenticationService.getSessionRole()
        });
    }

    getAvailableLocations() {
        return this.http.get(
            `${environment.apiUrl}/location/user/${this.authenticationService.getSessionRole()}`
        );
    }

    getAllChannelsGroupByResolution(locationsRes) {
        let resolutionMap = new Map();
        if (locationsRes) {
            locationsRes.countries.forEach(country => {
                country.cities.forEach(city => {
                    city.zones.forEach(zone => {
                        zone.channels.forEach(channel => {
                            if (channel.groups.length > 1) {
                                let key = '';
                                channel.groups.forEach(function (g) {
                                    if (key !== '') {
                                        key += ', ';
                                    }
                                    key += g.width + 'x' + g.height;
                                });
                                key = channel.name + ' ' + key;
                                resolutionMap.set(key, [channel]);
                            } else {
                                let key = channel.groups[0].width + 'x' + channel.groups[0].height;
                                if (resolutionMap.has(key)) {
                                    let currentChannels = resolutionMap.get(key);
                                    currentChannels.push(channel);
                                    resolutionMap.set(key, currentChannels);
                                } else {
                                    resolutionMap.set(key, [channel]);
                                }
                            }
                        });
                    });
                });
            });
            this.allChannelsGrouped = resolutionMap;
        }
        return resolutionMap;
    }

    getSelectedChannelsGroupByResolution(selectedChannels) {
        let allChannels = this.allChannelsGrouped;
        let newMapChannelsSelected = new Map();
        let multiResolutionMap = new Map();
        let resolutions = [];
        for (let [key, channels] of allChannels) {
            let newChannelsSelected = [];
            for (let j = 0; j < channels.length; j++) {
                for (let i = 0; i < selectedChannels.length; i++) {
                    resolutions = [];
                    if (selectedChannels[i] == channels[j].idChannel) {
                        newChannelsSelected.push(channels[j]);
                        channels[j].groups.forEach(function (g) {
                            resolutions.push({
                                width: g.width,
                                height: g.height,
                                idGroup: g.idGroup
                            });
                        });
                        multiResolutionMap.set(channels[j].idChannel, resolutions);
                    }
                }
            }
            if (newChannelsSelected.length > 0)
                newMapChannelsSelected.set(key, newChannelsSelected);
        }

        this.allMultiResolutionChannelsGrouped = multiResolutionMap;
        return newMapChannelsSelected;
    }

    getMultiResolution(channelId) {
        return this.allMultiResolutionChannelsGrouped?.get(channelId) || [];
    }

    /**
     * Groups the campaign's BOOKED channels by resolution for the media plan. Sourced from
     * `channelsDetails` (every booked channel, resolved by id regardless of zone/role and carrying
     * the synthesized `groups`), so channels that are absent from the location tree — e.g. ones with
     * no resolved zone, or linked siblings outside the user's available locations — are still
     * detected. The location-tree entry is preferred when available so its `ruleValues` are kept.
     */
    groupBookedChannelsByResolution(locationsRes: any, channelsDetails: any[], selectedChannelIds: any[]) {
        const treeById = new Map<any, any>();
        if (locationsRes) {
            (locationsRes.countries || []).forEach((co: any) =>
                (co.cities || []).forEach((ci: any) =>
                    (ci.zones || []).forEach((z: any) =>
                        (z.channels || []).forEach((ch: any) => treeById.set(ch.idChannel, ch)))));
        }
        const detailsById = new Map<any, any>((channelsDetails || []).map((c: any) => [c.idChannel, c]));

        const resolutionMap = new Map();
        const multiResolutionMap = new Map();
        const seen = new Set();

        (selectedChannelIds || []).forEach(id => {
            if (seen.has(id)) return;
            seen.add(id);
            // Prefer the location-tree channel (has ruleValues); fall back to channelsDetails.
            const channel = treeById.get(id) || detailsById.get(id);
            if (!channel) return;

            const groups = channel.groups || [];
            multiResolutionMap.set(channel.idChannel, groups.map((g: any) => ({
                width: g.width,
                height: g.height,
                idGroup: g.idGroup
            })));

            if (groups.length > 1) {
                const key = channel.name + ' ' + groups.map((g: any) => g.width + 'x' + g.height).join(', ');
                resolutionMap.set(key, [channel]);
            } else {
                const g0 = groups[0] || {};
                const key = g0.width + 'x' + g0.height;
                if (resolutionMap.has(key)) {
                    resolutionMap.get(key).push(channel);
                } else {
                    resolutionMap.set(key, [channel]);
                }
            }
        });

        this.allMultiResolutionChannelsGrouped = multiResolutionMap;
        this.allChannelsGrouped = resolutionMap;
        return resolutionMap;
    }

    uploadMedia(file) {
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        return this.http.post<any>(`${environment.apiUrl}/iintegration/campaign-media/upload`, formData).pipe(
            tap(response => {
                return response;
            })
        );
    }

    getMedia(fileName) {
        return this.http.get(`${environment.apiUrl}/iintegration/media/${fileName}`, {
            responseType: 'blob'
        });
    }
}
